import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import logo from '@/assets/logo.svg';
import { Button } from '@/components/ui/button';
import { GenerateOfficeDoctorAttendingAccountingResponse } from '@/hooks/mutations/use-generate-office-doctor-attending-accounting';
import { useAutoExportAccoutingDoctorAttendingPdfViewModel } from './hooks/use-auto-export.viewmodel';
import { useExportAccountingDoctorAttendingToPdfViewModel } from './hooks/use-export-to-pdf.viewmodel';
import { useAccoutingDoctorAttendingViewModel } from './use-accounting-doctor-attending.viewmodel';

type AccountingDoctorAttendingData = {
  accounting: GenerateOfficeDoctorAttendingAccountingResponse;

  officeName: string;
};
const defaultData: AccountingDoctorAttendingData = {
  accounting: {
    id: 'fake-uuid',
    period: {
      from: '2025-04-01',
      to: '2025-04-31',
    },
    amountFeeCollected: 1500, // Total honoraires perçus
    amountMedicalAssistantFee: 200, // Coût mensuel assistante médicale après aide CPAM
    amountSubstituteMedicalAssistantFee: 150, // Coût assistante remplaçants
    amountPaid: 1150, // Total honoraires encaissés par les remplaçants
    amountPayBack: 1000, // Total honoraires rétrocédés
    amountCommissionFee: 100, // Redevance mensuelle
    amountFee: 50, // Total honoraires
    attendingShare: 50, // Quote-part titulaire
    amountAvailable: 100, // Total disponible
    medicalAssistantShare: {
      doctorDuration: 48.5,
      siteDuration: 150.0,
      sharePercentage: 32.33,
    },
    doctorAttending: {
      id: 'fake-doctor',
      firstname: 'Jean',
      lastname: 'Metzger',
    },
  },
  officeName: 'Hagenau',
};

export const AccountingDoctorAttending = ({
  data,
  autoExport = false,
  onExportSuccess,
  onExportFailed,
}: {
  data?: AccountingDoctorAttendingData;
  autoExport?: boolean;
  onExportSuccess?: () => void;
  onExportFailed?: () => void;
}) => {
  const { t } = useTranslation();

  // @comment: default data to test template without data
  if (!data) {
    data = defaultData;
  }

  const viewModel = useAccoutingDoctorAttendingViewModel({
    accounting: data.accounting,
    officeName: data.officeName,
  });
  const exportToPdfViewModel = useExportAccountingDoctorAttendingToPdfViewModel(
    {
      information: viewModel.information,
    },
  );
  useAutoExportAccoutingDoctorAttendingPdfViewModel({
    autoExport,
    pageLoaded: viewModel.pageLoaded,
    ressourceLoaded: viewModel.logoLoaded,
    exportToPdfViewModel,
    accountingId: data.accounting.id,
    onSuccess: onExportSuccess,
    onFailed: onExportFailed,
  });

  if (!viewModel.pageLoaded || !viewModel.information) {
    return null;
  } else if (viewModel.unauthorized) {
    if (autoExport) {
      onExportFailed?.();
      return null;
    } else {
      return <Navigate to="/" />;
    }
  }

  return (
    <div className="min-h-screen w-full">
      <div className="flex min-h-screen flex-col">
        <header className="flex h-[60px] items-center justify-between border-b bg-muted/40 px-4">
          <h1 className="font-semibold text-3xl">
            {t('accounting-doctor-attending.title')}
          </h1>
          <div className="flex gap-2">
            <Button
              onClick={exportToPdfViewModel.export}
              loading={exportToPdfViewModel.exporting}
            >
              {t('accounting-doctor-attending.pdf-template.export-btn')}
            </Button>
          </div>
        </header>

        <main
          ref={exportToPdfViewModel.contentRef}
          className="container mx-auto flex h-[1024px] min-h-full flex-1 flex-col gap-12 p-12"
        >
          {/* Logo */}
          <div className="flex h-fit items-center justify-center p-6">
            <img
              src={logo}
              onLoad={() => {
                viewModel.setLogoLoaded(true);
              }}
              alt="omecare"
              className="h-10 w-auto"
            />
          </div>

          {/* Header */}
          <div className="flex flex-col gap-2">
            <span>
              {t('accounting-doctor-attending.pdf-template.header.created-on', {
                date: viewModel.information.date,
              })}
            </span>

            {/* Doctor attending */}
            <span>
              {t(
                viewModel.information.doctorAttending.fullname.key,
                viewModel.information.doctorAttending.fullname.params,
              )}
            </span>

            {/* Accounting of / on period */}
            <span>
              {t(
                'accounting-doctor-attending.pdf-template.header.doctor-attending-accounting-on-period',
                {
                  officeName: viewModel.information.officeName,
                  periodFrom: viewModel.information.periodFrom,
                  periodTo: viewModel.information.periodTo,
                },
              )}
            </span>
          </div>

          {/* Summary */}
          <div className="flex flex-col gap-2">
            {/* Accounting summary */}
            <div className="flex flex-col">
              <span>
                {t(
                  'accounting-doctor-attending.pdf-template.summary.amount-collected',
                  {
                    total: viewModel.information.amountFeeCollected,
                  },
                )}
              </span>
              <span>
                {t(
                  'accounting-doctor-attending.pdf-template.summary.amount-medical-assistant-fee',
                  {
                    total: viewModel.information.amountMedicalAssistantFee,
                  },
                )}
              </span>
              <span>
                {t(
                  'accounting-doctor-attending.pdf-template.summary.amount-paid',
                  {
                    total: viewModel.information.amountPaid,
                  },
                )}
              </span>
              <span>
                {t(
                  'accounting-doctor-attending.pdf-template.summary.amount-substitute-medical-assistant-fee',
                  {
                    total:
                      viewModel.information.amountSubstituteMedicalAssistantFee,
                  },
                )}
              </span>
              <span>
                {t(
                  'accounting-doctor-attending.pdf-template.summary.amount-pay-back',
                  {
                    total: viewModel.information.amountPayBack,
                  },
                )}
              </span>
              <span>
                {t(
                  'accounting-doctor-attending.pdf-template.summary.amount-commission-fee',
                  {
                    total: viewModel.information.amountCommissionFee,
                  },
                )}
              </span>
            </div>

            {/* Accounting total */}
            <div className="flex flex-col">
              <span>
                {t(
                  'accounting-doctor-attending.pdf-template.summary.amount-fee-before-payback',
                  {
                    total: viewModel.information.amountFeeBeforePayback,
                  },
                )}
              </span>
              <span>
                {t(
                  'accounting-doctor-attending.pdf-template.summary.amount-fee',
                  {
                    total: viewModel.information.amountFee,
                  },
                )}
              </span>
              <span>
                {t(
                  'accounting-doctor-attending.pdf-template.summary.amount-available',
                  {
                    total: viewModel.information.amountAvailable,
                  },
                )}
              </span>
              <span>
                {t(
                  'accounting-doctor-attending.pdf-template.summary.attending-share',
                  {
                    total: viewModel.information.attendingShare,
                  },
                )}
              </span>
            </div>

            {/* Medical Assistant Share */}
            {viewModel.information.medicalAssistantShare && (
              <div className="mt-4 flex flex-col rounded-lg border bg-muted/20 p-4">
                <h3 className="mb-2 font-semibold">
                  {t(
                    'accounting-doctor-attending.pdf-template.medical-assistant-share.title',
                  )}
                </h3>
                <span>
                  {t(
                    'accounting-doctor-attending.pdf-template.medical-assistant-share.doctor-duration',
                    {
                      duration: (
                        viewModel.information.medicalAssistantShare
                          .doctorDuration / 60
                      ).toFixed(1),
                    },
                  )}
                </span>
                <span>
                  {t(
                    'accounting-doctor-attending.pdf-template.medical-assistant-share.site-duration',
                    {
                      duration: (
                        viewModel.information.medicalAssistantShare
                          .siteDuration / 60
                      ).toFixed(1),
                    },
                  )}
                </span>
                <span className="font-medium">
                  {t(
                    'accounting-doctor-attending.pdf-template.medical-assistant-share.percentage',
                    {
                      percentage:
                        viewModel.information.medicalAssistantShare
                          .sharePercentage,
                    },
                  )}
                </span>
              </div>
            )}
          </div>

          {/* Signature */}
          <div className="flex flex-col text-left">
            <span>
              {t(
                'accounting-doctor-attending.pdf-template.signature.best-regards',
              )}
              ,
            </span>
            <span>
              {t(
                'accounting-doctor-attending.pdf-template.signature.secretary-job-title',
              )}
            </span>
          </div>

          {/* Footer */}
          <div className="absolute bottom-0 left-1/2 flex w-[320px] -translate-x-1/2 -translate-y-1/2 transform flex-col text-center">
            <span>OMECARE</span>
            <span>26 rue du Marechal Foch 67500 Haguenau.</span>
            <span>Tel : 03 88 05 22 83 </span>
            <span>SIRET : 978 062 834 00012</span>
            <a
              className="font-medium text-blue-600 underline hover:no-underline dark:text-blue-500"
              href="www.omecare.fr"
            >
              www.omecare.fr
            </a>
          </div>
        </main>
      </div>
    </div>
  );
};
